import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import TextInputField from 'components/TextInputField';
import ManaFilters from 'components/ManaFilters/ManaFilters';
import Accordion from './Components/Accordion';
import AdvancedFilters from './Components/AdvancedFilters';
import MagicButton from 'components/MagicButton/MagicButton';
import QuickSearchBar from './Components/QuickSearchBar';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { StatsDropdown } from './Components/StatsDropdown';
import { AssetUrls } from 'config/assets';
import { sanitizeSearchQuery, sanitizeText, sanitizeCardName } from 'utils/inputSanitization';
import './SearchPage.css';

interface SearchFilters {
  // Text fields
  name?: string;
  oracleText?: string;
  typeLine?: string;
  setName?: string;
  manaCost?: string;
  
  // Colors
  colors?: string[];
  
  // Stats
  cmc?: number;
  cmc_comparison?: 'eq' | 'gt' | 'lt';
  power?: string;
  power_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  toughness?: string;
  toughness_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  
  // Advanced filters
  artist?: string;
  flavorText?: string;
  lang?: string;
  format?: string;
  formatLegality?: 'legal' | 'not_legal';
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [quickSearch, setQuickSearch] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [cmcValue, setCmcValue] = useState('');
  const [cmcComparison, setCmcComparison] = useState('equal to');
  const [powerValue, setPowerValue] = useState('');
  const [powerComparison, setPowerComparison] = useState('eq');
  const [toughnessValue, setToughnessValue] = useState('');
  const [toughnessComparison, setToughnessComparison] = useState('eq');

  const updateFilter = useCallback((key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = () => {
    // Build search parameters
    const searchParams: any = {};
    
    // Use quickSearch for name if no specific card name is provided (sanitized)
    if (quickSearch && !filters.name) {
      searchParams.name = sanitizeCardName(quickSearch);
    }
    
    // Add all filters with proper sanitization
    if (filters.name) searchParams.name = sanitizeCardName(filters.name);
    if (filters.oracleText) searchParams.oracleText = sanitizeSearchQuery(filters.oracleText);
    if (filters.flavorText) searchParams.flavorText = sanitizeSearchQuery(filters.flavorText);
    if (filters.typeLine) searchParams.typeLine = sanitizeText(filters.typeLine);
    if (filters.setName) searchParams.setName = sanitizeCardName(filters.setName);
    if (filters.manaCost) searchParams.manaCost = sanitizeText(filters.manaCost);
    if (selectedColors.length > 0) searchParams.colors = selectedColors.join(',');
    
    // Handle CMC
    if (cmcValue) {
      const cmcComparisonMap: { [key: string]: 'eq' | 'gt' | 'lt' } = {
        'equal to': 'eq',
        'greater than': 'gt',
        'less than': 'lt'
      };
      searchParams.cmc = parseInt(cmcValue);
      searchParams.cmc_comparison = cmcComparisonMap[cmcComparison];
    }
    
    // Handle Power
    if (powerValue) {
      searchParams.power = powerValue;
      searchParams.power_comparison = powerComparison;
    }
    
    // Handle Toughness
    if (toughnessValue) {
      searchParams.toughness = toughnessValue;
      searchParams.toughness_comparison = toughnessComparison;
    }
    
    // Add advanced filters
    if (filters.artist) searchParams.artist = filters.artist;
    if (filters.lang) searchParams.lang = filters.lang;
    if (filters.format) searchParams.format = filters.format;
    if (filters.formatLegality) searchParams.formatLegality = filters.formatLegality;
    
    // Navigate to search results with parameters
    const queryString = new URLSearchParams(searchParams).toString();
    navigate(`/search/results?${queryString}`);
  };

  const handleQuickSearchChange = (value: string) => {
    setQuickSearch(value);
  };

  const handleQuickSearch = (searchValue?: string) => {
    // Use the directly passed value if available, otherwise fall back to state
    const searchTerm = searchValue ?? quickSearch;
    if (searchTerm) {
      navigate(`/search/results?name=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleColorToggle = (color: string) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  // Handle Enter key press from any form field
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch(); // Perform search with all current filters
    }
  };

  return (
    <div className="search-page-container">
      <GlassContainer className="search-glass-container">
        {/* Header with QuickSearchBar */}
        <div className="search-header">
          <QuickSearchBar
            placeholder="Quick Search"
            onSearchChange={handleQuickSearchChange}
            onSearch={handleQuickSearch}
          />
        </div>

        {/* Body with Filters */}
        <div className="search-body">
          {/* Page Title */}
          <div className="search-page-title">
            <h1>Search for Cards</h1>
          </div>
          
          <div className="search-filters-container">
            {/* Left Column */}
            <div className="filters-column filters-left">
              <div className="filter-group">
                <h3 className="filter-group-title">Card Details</h3>
                
                {/* Card Name */}
                <div className="filter-block">
                  <label>Card Name</label>
                  <TextInputField
                    placeholder="Any words in the name"
                    value={filters.name || ''}
                    onChange={(value) => updateFilter('name', value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                {/* Oracle Text */}
                <div className="filter-block">
                  <label>Oracle Text</label>
                  <TextInputField
                    placeholder='Any text, e.g. "Draw a card"'
                    value={filters.oracleText || ''}
                    onChange={(value) => updateFilter('oracleText', value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                {/* Type Line */}
                <div className="filter-block">
                  <label>Type Line</label>
                  <TextInputField
                    placeholder='Any type or sub-type e.g. "Human"'
                    value={filters.typeLine || ''}
                    onChange={(value) => updateFilter('typeLine', value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                {/* Sets */}
                <div className="filter-block">
                  <label>Sets</label>
                  <TextInputField
                    placeholder="Set or expansion name"
                    value={filters.setName || ''}
                    onChange={(value) => updateFilter('setName', value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="filters-column filters-right">
              <div className="filter-group">
                <h3 className="filter-group-title">Colors & Stats</h3>
                
                {/* Colors */}
                <div className="filter-block">
                  <label>Colors</label>
                  <ManaFilters 
                    selectedColors={selectedColors}
                    onColorToggle={handleColorToggle}
                  />
                </div>

                {/* Mana Cost */}
                <div className="filter-block">
                  <label>Mana Cost</label>
                  <TextInputField
                    placeholder='Any mana symbols, e.g. "{W}{W}"'
                    value={filters.manaCost || ''}
                    onChange={(value) => updateFilter('manaCost', value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                {/* Mana Value (CMC) */}
                <div className="filter-block">
                  <label>Mana Value</label>
                  <div className="stats-row">
                    <StatsDropdown
                      options={['equal to', 'greater than', 'less than']}
                      value={cmcComparison}
                      onChange={setCmcComparison}
                      minWidth={110}
                    />
                    <TextInputField
                      placeholder='Enter value'
                      value={cmcValue}
                      onChange={setCmcValue}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>

                {/* Power */}
                <div className="filter-block">
                  <label>Power</label>
                  <div className="stats-row">
                    <StatsDropdown
                      options={['Equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to']}
                      value={powerComparison === 'eq' ? 'Equal to' : 
                             powerComparison === 'gt' ? 'Greater than' :
                             powerComparison === 'gte' ? 'Greater than or equal to' :
                             powerComparison === 'lt' ? 'Less than' : 'Less than or equal to'}
                      onChange={(value) => setPowerComparison(
                        value === 'Equal to' ? 'eq' :
                        value === 'Greater than' ? 'gt' :
                        value === 'Greater than or equal to' ? 'gte' :
                        value === 'Less than' ? 'lt' : 'lte'
                      )}
                      minWidth={180}
                    />
                    <TextInputField
                      placeholder='Enter value (0-99)'
                      value={powerValue}
                      onChange={setPowerValue}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>

                {/* Toughness */}
                <div className="filter-block">
                  <label>Toughness</label>
                  <div className="stats-row">
                    <StatsDropdown
                      options={['Equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to']}
                      value={toughnessComparison === 'eq' ? 'Equal to' : 
                             toughnessComparison === 'gt' ? 'Greater than' :
                             toughnessComparison === 'gte' ? 'Greater than or equal to' :
                             toughnessComparison === 'lt' ? 'Less than' : 'Less than or equal to'}
                      onChange={(value) => setToughnessComparison(
                        value === 'Equal to' ? 'eq' :
                        value === 'Greater than' ? 'gt' :
                        value === 'Greater than or equal to' ? 'gte' :
                        value === 'Less than' ? 'lt' : 'lte'
                      )}
                      minWidth={180}
                    />
                    <TextInputField
                      placeholder='Enter value (0-99)'
                      value={toughnessValue}
                      onChange={setToughnessValue}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Filters Section */}
          <div className="advanced-filters-section">
            <Accordion title="More Search Filters">
              <AdvancedFilters 
                filters={filters}
                onFilterChange={updateFilter}
                onKeyDown={handleKeyDown}
              />
            </Accordion>
          </div>

          {/* Search Action Section */}
          <div className="search-action-section">
            <div className="search-button-container">
              <MagicButton
                label="Search Cards"
                icon={
                  <img
                    src={AssetUrls.search.searchIcon}
                    alt="Search"
                    style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                  />
                }
                color="purple"
                onPress={handleSearch}
              />
            </div>
          </div>
        </div>
      </GlassContainer>
    </div>
  );
}
